import React from 'react';
import { Link } from 'react-router-dom';
import StaticPageLayout from '../components/StaticPageLayout';
import { CheckCircle, Target, Eye, Globe, BookOpen, Users, Award } from 'lucide-react';

const AboutPage = () => (
  <StaticPageLayout
    overline="About"
    title="About the Institute"
    description="MAH Quantum Research Institute is a structured academic publishing platform designed to support rigorous, standards-driven research publication across scientific and technological disciplines."
    breadcrumbs={[{ label: 'About the Institute', path: '/about' }]}
  >
    <div className="space-y-12">

      {/* Who We Are */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Who We Are</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          MAH Quantum Research Institute is an academic and professional research publishing platform committed to providing a transparent, structured, and standards-driven environment for researchers, scholars, and independent professionals to disseminate their work.
        </p>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          The Institute was founded on the conviction that rigorous, peer-reviewed research publication should be accessible to every qualified researcher — regardless of institutional affiliation, geography, or career stage. We are building infrastructure that meets the expectations of the global scholarly community while remaining open and inclusive.
        </p>
        <p className="text-[#4B5563] leading-relaxed">
          Our editorial process is guided by the principles of the Committee on Publication Ethics (COPE) and is supported by an international advisory structure composed of domain experts from across academia and research.
        </p>
      </section>

      {/* Mission & Values */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-6 pb-3 border-b border-[#E5E7EB]">Mission &amp; Core Values</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              icon: <Target size={16} className="text-[#1A73E8]" />,
              title: 'Mission',
              desc: 'To provide a credible, accessible, and rigorous research publication platform that upholds the highest standards of academic integrity for all researchers, irrespective of affiliation.'
            },
            {
              icon: <Eye size={16} className="text-[#1A73E8]" />,
              title: 'Transparency',
              desc: 'Every stage of the editorial and review process is communicated to authors. There are no hidden processes, undisclosed fees, or opaque evaluation criteria.'
            },
            {
              icon: <CheckCircle size={16} className="text-[#1A73E8]" />,
              title: 'Standards',
              desc: 'All submissions are evaluated against defined academic and methodological standards, consistent with global best practices in scholarly publishing.'
            },
            {
              icon: <Globe size={16} className="text-[#1A73E8]" />,
              title: 'Accessibility',
              desc: 'Open to academic researchers, professors, PhD scholars, and independent research professionals — globally and without unnecessary institutional barriers.'
            },
            {
              icon: <Award size={16} className="text-[#1A73E8]" />,
              title: 'Integrity',
              desc: 'We uphold strict publication ethics aligned with COPE guidelines, including policies on authorship, conflict of interest, plagiarism, and data integrity.'
            },
            {
              icon: <Users size={16} className="text-[#1A73E8]" />,
              title: 'Community',
              desc: 'We are committed to building a scholarly community that supports researchers at every career stage, from PhD scholars to senior professors and independent researchers.'
            },
          ].map(item => (
            <div key={item.title} className="border border-[#E5E7EB] p-5 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 bg-[#EFF6FF] border border-[#BFDBFE] flex items-center justify-center">{item.icon}</div>
                <h3 className="font-semibold text-[#1F1F1F] text-sm">{item.title}</h3>
              </div>
              <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Research Scope */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Research Scope &amp; Disciplines</h2>
        <p className="text-[#4B5563] text-sm leading-relaxed mb-4">
          MAH Quantum Research Institute publishes across a broad range of scientific and technological disciplines. Our journals are structured to accommodate both highly specialized and interdisciplinary research:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { icon: <BookOpen size={14} className="text-[#1A73E8]" />, label: 'Quantum Computing & Information Systems' },
            { icon: <BookOpen size={14} className="text-[#1A73E8]" />, label: 'Materials Science & Nanotechnology' },
            { icon: <BookOpen size={14} className="text-[#1A73E8]" />, label: 'Biomedical Sciences & Molecular Biology' },
            { icon: <BookOpen size={14} className="text-[#1A73E8]" />, label: 'Artificial Intelligence & Machine Learning' },
            { icon: <BookOpen size={14} className="text-[#1A73E8]" />, label: 'Applied Mathematics & Computational Science' },
            { icon: <BookOpen size={14} className="text-[#1A73E8]" />, label: 'Environmental Science & Sustainable Engineering' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-3 border border-[#E5E7EB] px-4 py-3 bg-white">
              <div className="w-6 h-6 bg-[#EFF6FF] border border-[#BFDBFE] flex items-center justify-center flex-shrink-0">{item.icon}</div>
              <span className="text-sm text-[#374151]">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Editorial Principles */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Editorial Principles</h2>
        <ul className="space-y-2.5">
          {[
            'All submissions undergo double-blind peer review by qualified domain experts',
            'Editorial decisions are based exclusively on scientific merit and methodological rigour',
            'Authors receive structured feedback at every stage of the review process',
            'COPE-compliant publication ethics policies govern all editorial decisions',
            'Accepted publications are assigned DOIs and archived via Zenodo for long-term open access',
            'No hidden fees; all submission requirements and review criteria are published openly',
          ].map(item => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-[#4B5563]">
              <CheckCircle size={14} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      {/* Contact */}
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Contact &amp; Enquiries</h2>
        <p className="text-[#4B5563] text-sm leading-relaxed mb-4">
          For enquiries about the platform, submission processes, editorial board membership, or institutional partnerships:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border border-[#E5E7EB] px-5 py-4 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF] mb-1">General Submissions</p>
            <p className="text-[#1A73E8] text-sm font-medium">submissions@mahquantum.tech</p>
          </div>
          <div className="border border-[#E5E7EB] px-5 py-4 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF] mb-1">Editorial Office</p>
            <p className="text-[#1A73E8] text-sm font-medium">info@mahquantum.tech</p>
          </div>
        </div>
        <p className="text-xs text-[#6B7280] mt-3">
          For detailed policies, see the <Link to="/author-guidelines" className="text-[#1A73E8] hover:underline">Author Guidelines</Link> and <Link to="/publication-ethics" className="text-[#1A73E8] hover:underline">Publication Ethics Policy</Link>.
        </p>
      </section>

    </div>
  </StaticPageLayout>
);

export default AboutPage;
